import { forwardRef, useCallback, useMemo } from 'react';

import { type ButtonProps } from '../../../atoms';
import { SelectAllToggleButton } from '../../select-all-toggle-button/SelectAllToggleButton';
import { useVirtualMultiselectRoot } from '../contexts/root.context';

type VirtualMultiselectSelectAllButtonProps = Omit<
  ButtonProps,
  'aria-label' | 'children' | 'size' | 'variant'
>;

export const VirtualMultiselectSelectAllButton = forwardRef<
  HTMLButtonElement,
  VirtualMultiselectSelectAllButtonProps
>(({ onClick, ...props }, ref) => {
  const { availableItemsSet, onValueChange, value } =
    useVirtualMultiselectRoot();

  const allSelected = useMemo(
    () =>
      availableItemsSet.size > 0 &&
      Array.from(availableItemsSet).every((id) => value.includes(id)),
    [availableItemsSet, value],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const availableItemIds = Array.from(availableItemsSet);

      if (allSelected) {
        onValueChange(value.filter((id) => !availableItemsSet.has(id)));
      } else {
        onValueChange(Array.from(new Set([...value, ...availableItemIds])));
      }
      onClick?.(e);
    },
    [allSelected, availableItemsSet, onClick, onValueChange, value],
  );

  return (
    <SelectAllToggleButton
      ref={ref}
      allSelected={allSelected}
      disabled={availableItemsSet.size === 0}
      itemsCount={availableItemsSet.size}
      onClick={handleClick}
      {...props}
    />
  );
});

VirtualMultiselectSelectAllButton.displayName =
  'VirtualMultiselectSelectAllButton';
